import api from './index';

/**
 * @typedef {Object} AdminAnnouncementsParams
 * @property {number} [page] Page index ≥ 1 (default 1).
 * @property {number} [limit] Page size 1–100 (default 20).
 * @property {string} [from] ISO date/time — filter createdAt ≥ from.
 * @property {string} [to] ISO date/time — filter createdAt ≤ to.
 * @property {string} [sentBy] Filter metadata.sentBy (e.g. `admin`).
 */

/**
 * @typedef {Object} AdminAnnouncementUser
 * @property {string} [_id]
 * @property {string} [name]
 * @property {string} [email]
 * @property {string} [role]
 */

/**
 * @typedef {Object} AdminAnnouncementRow
 * @property {string} _id
 * @property {AdminAnnouncementUser} [userId]
 * @property {string} title
 * @property {string} message
 * @property {string} type
 * @property {boolean} isRead
 * @property {string} [referenceId]
 * @property {string} [referenceType]
 * @property {Record<string, unknown>} [metadata]
 * @property {string} createdAt
 * @property {string} [updatedAt]
 */

/**
 * @typedef {Object} AdminAnnouncementsResponse
 * @property {AdminAnnouncementRow[]} data
 * @property {number} total
 * @property {number} page
 * @property {number} limit
 * @property {number} totalPages
 */

/**
 * Validates and normalizes listing params (client-side guardrails).
 * @param {AdminAnnouncementsParams} [params]
 * @returns {Required<Pick<AdminAnnouncementsParams, 'page' | 'limit'>> & AdminAnnouncementsParams}
 */
export function validateAdminAnnouncementParams(params = {}) {
  const rawPage = parseInt(String(params.page ?? 1), 10);
  const page = Number.isFinite(rawPage) && rawPage >= 1 ? rawPage : 1;

  let rawLimit = parseInt(String(params.limit ?? 20), 10);
  if (!Number.isFinite(rawLimit) || rawLimit < 1) rawLimit = 20;
  const limit = Math.min(100, rawLimit);

  return {
    page,
    limit,
    from: params.from?.trim() || undefined,
    to: params.to?.trim() || undefined,
    sentBy: params.sentBy?.trim() || undefined
  };
}

/**
 * Stable React Query key for admin announcement notifications.
 * @param {AdminAnnouncementsParams} params
 * @returns {readonly unknown[]}
 */
export function adminAnnouncementsQueryKey(params) {
  const v = validateAdminAnnouncementParams(params);
  return ['admin', 'announcements', v.page, v.limit, v.from ?? null, v.to ?? null, v.sentBy ?? null];
}

/**
 * GET /notifications/admin/announcements — admin / superAdmin only.
 * @param {AdminAnnouncementsParams} params
 * @returns {Promise<AdminAnnouncementsResponse>}
 */
export async function getAdminAnnouncements(params) {
  const v = validateAdminAnnouncementParams(params);
  const search = new URLSearchParams();
  search.set('page', String(v.page));
  search.set('limit', String(v.limit));
  if (v.from) search.set('from', v.from);
  if (v.to) search.set('to', v.to);
  if (v.sentBy) search.set('sentBy', v.sentBy);

  const { data } = await api.get(`/notifications/admin/announcements?${search.toString()}`);
  return data;
}
